import api from '../client';

/**
 * Service pour la gestion des conversations
 */
const conversationService = {
  /**
   * Récupérer la liste des conversations
   * @param {Object} params - Paramètres (agentId, status, page, limit)
   * @returns {Promise} Liste des conversations
   */
  getConversations: async (params = {}) => {
    try {
      const response = await api.get('/conversations', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer une conversation par son ID
   * @param {string} id - ID de la conversation
   * @returns {Promise} Données de la conversation
   */
  getConversationById: async (id) => {
    try {
      const response = await api.get(`/conversations/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer une nouvelle conversation
   * @param {Object} conversationData - Données de la conversation
   * @returns {Promise} Conversation créée
   */
  createConversation: async (conversationData) => {
    try {
      const response = await api.post('/conversations', conversationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les messages d'une conversation
   * @param {string} id - ID de la conversation
   * @param {Object} params - Paramètres (page, limit)
   * @returns {Promise} Liste des messages
   */
  getMessages: async (id, params = {}) => {
    try {
      const response = await api.get(`/conversations/${id}/messages`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Envoyer un message dans une conversation
   * @param {string} id - ID de la conversation
   * @param {Object} messageData - Données du message (content, sender)
   * @returns {Promise} Message envoyé et réponse IA
   */
  sendMessage: async (id, messageData) => {
    try {
      const response = await api.post(`/conversations/${id}/messages`, messageData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour le statut d'une conversation
   * @param {string} id - ID de la conversation
   * @param {string} status - Nouveau statut (active, closed, archived)
   * @returns {Promise} Conversation mise à jour
   */
  updateStatus: async (id, status) => {
    try {
      const response = await api.patch(`/conversations/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Assigner une conversation à un agent humain
   * @param {string} id - ID de la conversation
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise} Conversation assignée
   */
  assignToUser: async (id, userId) => {
    try {
      const response = await api.patch(`/conversations/${id}/assign`, { userId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer une conversation
   * @param {string} id - ID de la conversation
   * @returns {Promise} Confirmation de suppression
   */
  deleteConversation: async (id) => {
    try {
      const response = await api.delete(`/conversations/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default conversationService;
